import React, { useEffect, useRef, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Chip, Divider, Button, Checkbox,
  ToggleButton, ToggleButtonGroup, Autocomplete, TextField, CircularProgress, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublicIcon from '@mui/icons-material/Public';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { accessGrantApi, AccessGrant, ScopeType, AccessLevel } from '../../../../api/accessGrantApi';
import { AdminUser } from '../../../../api/adminUsersApi';
import { rolesV2Api } from '../../../../api/rolesV2Api';
import { userTenantZonesApi, InsightsRegionZones } from '../../../../api/userTenantZonesApi';
import { isBroadScope, roleHasInsightsClient, recordBroadGrantRemoval } from '../../../../api/insightsGrantPolicy';
import { clientReportsApi } from '../../../../api/clientReportsApi';
import { ALL_REGIONS_KEY, DEFAULT_REGION_SLOT } from '../../../Insights/types';
import { INSIGHTS_MANAGER } from '../../../../auth/useInsightsAccess';
import { colors, typo } from '../../../../theme/tokens';
import EntityLabel from './EntityLabel';
import AssignedGroupsSection, { GroupDerivedAccessList } from './AssignedGroups';
import { useScopeEntities, ScopeOption } from '../hooks/useScopeEntities';

interface Props {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

type Option = ScopeOption;

const SCOPE_LABEL: Record<ScopeType, string> = {
  global: 'All tenants', customer: 'Customer', sponsor: 'Sponsor', tenant: 'Tenant',
};

/**
 * Synthetic "All Regions" toggle token — represents the UNRESTRICTED mode in the
 * multi-select. It is NOT a region slot: it can never be stored and is stripped
 * from the individual-region options. The leading space makes it impossible to
 * collide with a real report slot (the server trims every submitted slot).
 */
const ALL_REGIONS = ' ALL_REGIONS';

/** Slots that are never an "individual region to restrict":
 *  - ALL_REGIONS_KEY ('All Regions') is the unrestricted CONCEPT, owned by the
 *    exclusive toggle above — never a peer chip.
 *  - DEFAULT_REGION_SLOT ('__default__') is un-gateable: `slotAllowed()` in
 *    server/src/routes/clientReports.ts returns true for it regardless of the
 *    stored zones, so offering it here would promise a restriction the gate
 *    never honours. */
const isUngatedSlot = (slot: string): boolean =>
  slot === ALL_REGIONS_KEY || slot === DEFAULT_REGION_SLOT;

const UNRESTRICTED: InsightsRegionZones = { mode: 'unrestricted', regions: [] };

/**
 * Per-(user, tenant) region restriction, shown under a tenant-scoped grant when
 * the edited user's role holds insights_client. Individual regions are derived
 * live from the DISTINCT `regionSlot` values of THAT tenant's reports; the
 * selection is stored server-side via `userTenantZonesApi` (super-admin only).
 *
 * "All Regions" is the EXCLUSIVE unrestricted state — never a peer chip: picking
 * any individual region drops it, and ticking every individual region collapses
 * back to it (so regions added to the tenant LATER stay included). A strict
 * subset is an explicit allow-list; deselecting to none is an explicit "no
 * regions" (mode 'restricted' with an empty list), never a silent revert to
 * unrestricted. A tenant whose reports carry no region dimension shows an info
 * state, never an empty control.
 *
 * `mode` — not the emptiness of `regions` — is the source of truth for whether
 * the user is restricted.
 */
function TenantRegionRestriction({ userId, tenantCode }: { userId: number; tenantCode: string }) {
  const qc = useQueryClient();

  const optionsQuery = useQuery({
    queryKey: ['tenant-report-regions', tenantCode],
    queryFn: async () => {
      const reports = await clientReportsApi.list(tenantCode);
      // Raw slot values (deduped, NOT case-folded) so they match the server's
      // case-sensitive comparison against the stored region_slot rows.
      const slots = new Set<string>();
      reports.forEach((r) => { if (r.regionSlot) slots.add(r.regionSlot); });
      return Array.from(slots).sort();
    },
  });

  const zonesQuery = useQuery<InsightsRegionZones>({
    queryKey: ['user-tenant-zones', userId, tenantCode],
    queryFn: () => userTenantZonesApi.get(userId, tenantCode),
  });

  const setMut = useMutation({
    mutationFn: (zones: InsightsRegionZones) => userTenantZonesApi.set(userId, tenantCode, zones),
    onSuccess: (saved) => {
      qc.setQueryData(['user-tenant-zones', userId, tenantCode], saved);
      qc.invalidateQueries({ queryKey: ['user-tenant-zones', userId, tenantCode] });
    },
  });

  if (optionsQuery.isLoading || zonesQuery.isLoading) {
    return <Box sx={{ py: 0.5 }}><CircularProgress size={14} sx={{ color: colors.textTertiary }} /></Box>;
  }

  const regions = (optionsQuery.data ?? []).filter((s) => !isUngatedSlot(s));
  if (regions.length === 0) {
    return (
      <Typography sx={{ ...typo.tinyText, color: colors.textTertiary, fontStyle: 'italic' }}>
        No regions to restrict for this tenant.
      </Typography>
    );
  }

  const stored = zonesQuery.data ?? UNRESTRICTED;
  const unrestricted = stored.mode === 'unrestricted';
  // Unrestricted shows ONLY the "All Regions" toggle; restricted shows individual
  // chips (never the toggle). Stale slots no report offers any more are dropped.
  const value = unrestricted ? [ALL_REGIONS] : stored.regions.filter((z) => regions.includes(z));

  // THE one canonical selection handler — maps every transition to the stored model.
  const commit = (raw: string[]) => {
    const nowHasAll = raw.includes(ALL_REGIONS);
    const individuals = raw.filter((x) => x !== ALL_REGIONS);
    let next: InsightsRegionZones;
    if (nowHasAll && !unrestricted) {
      // Ticked "All Regions" from restricted mode → unrestricted (clears individuals).
      next = UNRESTRICTED;
    } else if (individuals.length > 0) {
      // Any individual region selected → restricted; "All Regions" auto-deselects
      // (even if it lingered in the raw array while adding the first individual).
      next = { mode: 'restricted', regions: individuals };
    } else if (unrestricted) {
      // Un-ticking the sole "All Regions" chip with nothing else picked — restore
      // whatever was preserved from this user's last explicit selection (the
      // backend deliberately keeps it across an unrestricted round-trip; see
      // server/src/routes/admin/insightsRegionZones.ts) rather than wiping to
      // explicit-none. A genuine "no regions" narrowing only happens by
      // deselecting from an ALREADY-restricted state (the final else below).
      next = { mode: 'restricted', regions: stored.regions.filter((z) => regions.includes(z)) };
    } else {
      // Already restricted, deselected everything — a deliberate narrowing to
      // "no regions", never a silent revert to unrestricted.
      next = { mode: 'restricted', regions: [] };
    }
    // Manually selecting EVERY individual region collapses to unrestricted (store
    // the mode, not the enumerated list) so regions added later still apply.
    if (next.mode === 'restricted' && regions.every((r) => next.regions.includes(r))) {
      next = UNRESTRICTED;
    }
    setMut.mutate(next);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ ...typo.tinyText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.textTertiary, mb: 0.5 }}>
        Region access
      </Typography>
      <Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        disabled={setMut.isPending}
        options={[ALL_REGIONS, ...regions]}
        value={value}
        getOptionLabel={(o) => (o === ALL_REGIONS ? 'All Regions' : o)}
        onChange={(_e, v) => commit(v as string[])}
        renderOption={(props, option, { selected }) => {
          const { key, ...li } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>;
          return (
            <li key={key} {...li}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
              {option === ALL_REGIONS ? 'All Regions' : option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} size="small"
            placeholder={value.length ? undefined : 'No regions — sees nothing'} />
        )}
      />
      <Typography sx={{ ...typo.tinyText, color: colors.textTertiary, mt: 0.25 }}>
        {unrestricted
          ? 'All regions — unrestricted (includes regions added later)'
          : value.length === 0
            ? 'No regions — sees nothing for this tenant'
            : `${value.length} of ${regions.length} regions`}
      </Typography>
    </Box>
  );
}

/**
 * Users-tab "Manage tenant access" drawer — individual grants (real
 * `accessGrantApi`) merged with group-derived access (CPT-1132/1133):
 * `GroupDerivedAccessList` (read-only, dashed "from group" rows — cleared
 * only by unassigning the group) and `AssignedGroupsSection` (assign/
 * unassign groups) below it. Names resolve through `EntityLabel`
 * (name-primary, code-demoted — decisions §22).
 */
const AccessGrantsDrawer: React.FC<Props> = ({ open, user, onClose, onNotify }) => {
  const qc = useQueryClient();
  const userId = user?.user_id ?? 0;

  const [scopeType, setScopeType] = useState<ScopeType>('tenant');
  const [scopeOption, setScopeOption] = useState<Option | null>(null);
  const [level, setLevel] = useState<AccessLevel>('read');

  const grantsQuery = useQuery<AccessGrant[]>({
    queryKey: ['access-grants', userId],
    queryFn: () => accessGrantApi.list(userId),
    enabled: open && userId > 0,
  });

  // Shared lookup hook — same queryKeys as GroupEditorDrawer/AssignedGroups so
  // data is served from cache on reopen without a duplicate fetch (AC2).
  const scopeEntities = useScopeEntities(open);

  // The EDITED user's effective permission set (NOT the current admin's): the
  // RBAC v2 role whose role_code matches `user.role_v2_code`. Fails closed —
  // no role code, or no matching role, means no permissions and no region UI.
  const rolesQuery = useQuery({
    queryKey: ['rbac-v2', 'roles'],
    queryFn: () => rolesV2Api.listRoles(),
    enabled: open && !!user?.role_v2_code,
  });
  const perms: readonly string[] =
    (user?.role_v2_code
      ? rolesQuery.data?.find((r) => r.role_code === user.role_v2_code)?.permissions
      : undefined) ?? [];
  /** Managers are always region-unrestricted — they get an info line, no picker. */
  const isInsightsManager = perms.includes(INSIGHTS_MANAGER);
  const isInsightsClient = roleHasInsightsClient(perms); // canonical read

  // Exclude tenants/customers/sponsors the user already has a grant for so
  // they don't appear as selectable options in the Grant access dropdown.
  const grantedCodes = new Set(
    (grantsQuery.data ?? [])
      .filter((g) => g.scope_type === scopeType)
      .map((g) => g.scope_code),
  );
  const options: Option[] = (scopeType !== 'global' ? scopeEntities.optionsFor(scopeType) : [])
    .filter((o) => !grantedCodes.has(o.code));

  const upsert = useMutation({
    mutationFn: () => accessGrantApi.upsert({
      userId,
      scopeType,
      scopeCode: scopeType === 'global' ? undefined : scopeOption?.code,
      accessLevel: level,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['access-grants', userId] });
      setScopeOption(null);
      onNotify('Access granted', 'success');
    },
    onError: () => onNotify('Failed to grant access', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => accessGrantApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['access-grants', userId] });
      onNotify('Access removed', 'success');
    },
    onError: () => onNotify('Failed to remove access', 'error'),
  });

  // Block-new guard (defense-in-depth alongside the disabled toggles): never allow
  // an insights_client user to be granted a broad (wildcard) scope.
  const blockedBroad = isInsightsClient && isBroadScope(scopeType);
  const canAdd = (scopeType === 'global' ? true : !!scopeOption) && !blockedBroad;
  const grants = grantsQuery.data ?? [];

  // BLOCK-NEW: an insights_client user is explicit-tenant-only — never let the
  // scope selector rest on a broad (wildcard) scope. (Existing broad grants are
  // handled by the AUTO-REMOVE effect below.)
  useEffect(() => {
    if (isInsightsClient && isBroadScope(scopeType)) setScopeType('tenant');
  }, [isInsightsClient, scopeType]);

  // AUTO-REMOVE (explicit-tenant-only enforcement): when the drawer loads a user
  // whose role includes insights_client, DELETE any existing broad (wildcard)
  // grants server-side and keep the per-tenant ones. The authoritative audit is
  // server-side; recordBroadGrantRemoval is only a UI breadcrumb.
  const autoRemoveBroad = useMutation({
    mutationFn: (id: number) => accessGrantApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-grants', userId] }),
  });
  // Grant ids are globally unique (server autoincrement), so a never-reset set keyed
  // by id is a robust once-only guard — and StrictMode-safe: it survives dev's
  // setup→cleanup→setup double-invoke without wiping between runs (a per-userId reset
  // effect would clear it mid-cycle and double-fire the DELETE).
  const sweptRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!open || userId <= 0 || grantsQuery.isLoading || !isInsightsClient) return;
    const broad = grants.filter((g) => isBroadScope(g.scope_type) && !sweptRef.current.has(g.id));
    if (broad.length === 0) return;
    broad.forEach((g) => {
      sweptRef.current.add(g.id);
      recordBroadGrantRemoval(userId, g);
      autoRemoveBroad.mutate(g.id);
    });
    onNotify(
      `Removed ${broad.length} broad grant${broad.length > 1 ? 's' : ''} — insights_client is explicit-tenant-only`,
      'info',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, isInsightsClient, grants, grantsQuery.isLoading]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 0 } }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ ...typo.sectionTitle }}>Tenant access</Typography>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{user?.email}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 12.5, color: colors.textTertiary }}>
          Grant read or write access to specific tenants, or to every tenant under a
          sponsor or customer. Effective access is the highest level of any matching
          grant. With no grants, the user sees no tenants (once enforcement is on).
        </Typography>

        {/* Current grants */}
        <Box>
          <Typography sx={{ ...typo.fieldLabel, mb: 0.25 }}>Current access</Typography>
          <Typography sx={{ ...typo.tinyText, mb: 1 }}>
            Effective access is the highest level (read &lt; write) across every source below.
          </Typography>
          <Typography sx={{ ...typo.tinyText, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75 }}>
            Individual grants
          </Typography>
          {grantsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : grants.length === 0 ? (
            <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: colors.textTertiary }}>
              No individual grants.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {grants.map((g) => (
                <Box key={g.id} sx={{
                  display: 'flex', flexDirection: 'column', gap: 1, px: 1.5, py: 1,
                  border: `1px solid ${colors.borderLight}`, borderRadius: '10px',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {g.scope_type === 'global'
                      ? <PublicIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
                      : <Chip size="small" label={SCOPE_LABEL[g.scope_type]} sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {g.scope_type === 'global' ? (
                        <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          All tenants
                        </Typography>
                      ) : (
                        <EntityLabel
                          scope={g.scope_type}
                          name={scopeEntities.nameFor(g.scope_type, g.scope_code)}
                          code={g.scope_code}
                        />
                      )}
                    </Box>
                    <Chip size="small" label={g.access_level}
                      color={g.access_level === 'write' ? 'warning' : 'default'}
                      sx={{ height: 20, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }} />
                    <Tooltip title="Remove">
                      <IconButton size="small" disabled={remove.isPending} onClick={() => remove.mutate(g.id)}>
                        <DeleteOutlineIcon sx={{ fontSize: 18, color: colors.errorIcon }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Per-(user, tenant) region restriction — tenant grants only,
                      role-gated: managers are unrestricted, clients pick, anyone
                      else gets no region UI. */}
                  {g.scope_type === 'tenant' && isInsightsManager && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PublicOutlinedIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                      <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>Sees all regions (manager)</Typography>
                    </Box>
                  )}
                  {g.scope_type === 'tenant' && isInsightsClient && !isInsightsManager && (
                    <TenantRegionRestriction userId={userId} tenantCode={g.scope_code} />
                  )}
                </Box>
              ))}
            </Box>
          )}

          {user && <GroupDerivedAccessList userId={userId} open={open} />}
        </Box>

        <Divider />

        {/* Add grant */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ ...typo.fieldLabel }}>Grant access</Typography>
          <ToggleButtonGroup exclusive size="small" value={scopeType}
            onChange={(_e, v) => { if (v && !(isInsightsClient && isBroadScope(v))) { setScopeType(v); setScopeOption(null); } }} sx={{ flexWrap: 'wrap' }}>
            {(['global', 'customer', 'sponsor', 'tenant'] as ScopeType[]).map((s) => (
              // BLOCK-NEW: insights_client is explicit-tenant-only → broad scopes disabled.
              <ToggleButton key={s} value={s} disabled={isInsightsClient && isBroadScope(s)}
                sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>
                {SCOPE_LABEL[s]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {isInsightsClient && (
            <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>
              This user’s role includes <strong>insights_client</strong> — access is explicit-tenant-only,
              so All tenants / Customer / Sponsor are disabled. Grant per-tenant access (each tenant’s
              report regions are configured individually).
            </Typography>
          )}

          {scopeType !== 'global' && (
            <Autocomplete
              size="small"
              options={options}
              value={scopeOption}
              onChange={(_e, v) => setScopeOption(v)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              renderOption={(props, o) => {
                const { key, ...liProps } = props as { key?: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                return (
                  <Box component="li" key={key} {...liProps} sx={{ display: 'block', py: 0.75 }}>
                    <EntityLabel scope={scopeType} name={o.name} code={o.code} />
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder={`Choose a ${scopeType}…`}
                  InputProps={{ ...params.InputProps, sx: { borderRadius: '10px' } }} />
              )}
            />
          )}

          <ToggleButtonGroup exclusive size="small" value={level} onChange={(_e, v) => { if (v) setLevel(v); }}>
            <ToggleButton value="read" sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>Read</ToggleButton>
            <ToggleButton value="write" color="warning" sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600 }}>Write</ToggleButton>
          </ToggleButtonGroup>

          <Button variant="contained" startIcon={<AddIcon />} disabled={!canAdd || upsert.isPending}
            onClick={() => upsert.mutate()}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', alignSelf: 'flex-start' }}>
            {upsert.isPending ? 'Granting…' : 'Grant access'}
          </Button>
        </Box>

        <Divider />

        {/* Assigned groups (additive) */}
        {user && <AssignedGroupsSection userId={userId} open={open} onNotify={onNotify} />}
      </Box>
    </Drawer>
  );
};

export default AccessGrantsDrawer;
